import { supabase } from "@/lib/supabase";
import type { RealtimeChannel, RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import type { Notification } from "../notifications.types";

export async function getNotifications(userId: string): Promise<Notification[]> {
  const { data, error } = await supabase.rpc("get_user_notifications", {
    p_user_id: userId,
  });

  if (error) {
    throw new Error(`Failed to fetch notifications: ${error.message}`);
  }

  return data as Notification[];
}

export async function markNotificationRead(notificationId: string): Promise<void> {
  const { error } = await supabase
    .from("notifications")
    .update({ read_at: new Date().toISOString() })
    .eq("id", notificationId);

  if (error) {
    throw new Error(`Failed to mark notification as read: ${error.message}`);
  }
}

export async function markNotificationUnread(notificationId: string): Promise<void> {
  const { error } = await supabase
    .from("notifications")
    .update({ read_at: null })
    .eq("id", notificationId);

  if (error) {
    throw new Error(`Failed to mark notification as unread: ${error.message}`);
  }
}

export async function markAllNotificationsRead(userId: string): Promise<void> {
  const { error } = await supabase
    .from("notifications")
    .update({ read_at: new Date().toISOString() })
    .eq("recipient_id", userId)
    .is("read_at", null);

  if (error) {
    throw new Error(`Failed to mark all notifications as read: ${error.message}`);
  }
}

export async function createMentionNotification({
  recipientId,
  type,
  containerId,
  sourceId,
  body,
}: {
  recipientId: string;
  type: "chat_mention" | "doc_mention" | "comment_mention";
  containerId: string;
  sourceId?: string | null;
  body?: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc("create_mention_notification", {
    p_recipient_id: recipientId,
    p_type: type,
    p_container_id: containerId,
    p_source_id: sourceId ?? null,
    p_body: body ?? null,
  });

  if (error) {
    console.error("Failed to create mention notification:", error.message);
  }
}

export async function deleteDocMentionNotification({
  recipientId,
  containerId,
}: {
  recipientId: string;
  containerId: string;
}): Promise<void> {
  const { error } = await supabase.rpc("delete_doc_mention_notification", {
    p_recipient_id: recipientId,
    p_container_id: containerId,
  });

  if (error) {
    console.error("Failed to delete doc mention notification:", error.message);
  }
}

export async function updateDocMentionNotificationBody({
  recipientId,
  containerId,
  body,
}: {
  recipientId: string;
  containerId: string;
  body: string;
}): Promise<void> {
  const { error } = await supabase.rpc("update_doc_mention_notification_body", {
    p_recipient_id: recipientId,
    p_container_id: containerId,
    p_body: body,
  });

  if (error) {
    console.error("Failed to update doc mention notification body:", error.message);
  }
}

export async function deleteNotification(notificationId: string): Promise<void> {
  const { error } = await supabase.from("notifications").delete().eq("id", notificationId);

  if (error) {
    throw new Error(`Failed to delete notification: ${error.message}`);
  }
}

export async function deleteAllReadNotifications(userId: string): Promise<void> {
  const { error } = await supabase
    .from("notifications")
    .delete()
    .eq("recipient_id", userId)
    .not("read_at", "is", null);

  if (error) {
    throw new Error(`Failed to delete read notifications: ${error.message}`);
  }
}

export function resolveNotificationSlugs(
  notification: Notification,
): { workspaceSlug: string; projectSlug: string } | null {
  if (!notification.workspace_slug || !notification.project_slug) return null;
  return {
    workspaceSlug: notification.workspace_slug,
    projectSlug: notification.project_slug,
  };
}

/**
 * The `type` of the notification row a realtime event carries, or null when
 * the event has none (a DELETE only carries the primary key).
 */
function changedNotificationType(
  payload: RealtimePostgresChangesPayload<Record<string, unknown>>,
): string | null {
  const row = payload.new as Record<string, unknown> | undefined;
  return typeof row?.type === "string" ? row.type : null;
}

/**
 * `callback` receives the changed notification's type (CR-203 S3.5 keys
 * join-request invalidation on it), or null after a (re)subscribe, when
 * events may have been missed, and for a DELETE.
 */
export function createNotificationsSubscription(
  userId: string,
  callback: (changedType: string | null) => void,
): RealtimeChannel {
  return supabase
    .channel(`notifications-${userId}`)
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "notifications",
        filter: `recipient_id=eq.${userId}`,
      },
      (payload) => callback(changedNotificationType(payload)),
    )
    .subscribe((status) => {
      // After a reconnect Supabase re-subscribes the channel and fires
      // SUBSCRIBED again. Invalidate so any events missed during the outage
      // are picked up immediately.
      if (status === "SUBSCRIBED") {
        callback(null);
      }
    });
}

export function removeNotificationsSubscription(channel: RealtimeChannel): void {
  void supabase.removeChannel(channel);
}
